import { useEffect, useState } from "react";
import { X, Plus, Trash2, Loader2, Upload, Star } from "lucide-react";
import { createLot, updateLot, fetchAdminLot, uploadMedia, mediaSrc } from "@/b2b/b2bApi";
import { CATEGORY_ORDER, CATEGORY_LABELS, CATEGORY_FIELDS, MANUAL_STATUS_OPTIONS } from "@/b2b/constants";

const CATS = CATEGORY_ORDER.filter((c) => c !== "all");
const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800";

const emptyItem = () => ({
  product_name: "", brand: "", model: "", processor: "", ram: "", storage: "", display: "",
  graphics: "", technology: "", form_factor: "", capacity: "", interface: "", health: "",
  power_on_hours: "", adapter_type: "", pin: "", wattage: "", ram_platform: "", ddr_generation: "",
  bus_speed: "", specifications: "", condition: "",
  original_quantity: 1, available_quantity: 1, price_per_unit: "", remarks: "",
});

export default function B2BLotEditor({ lotId, onClose, onSaved }) {
  const isEdit = !!lotId;
  const [form, setForm] = useState({ lot_number: "", category: "laptops", lot_name: "", remarks: "", video_url: "", status: "" });
  const [items, setItems] = useState([emptyItem()]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    fetchAdminLot(lotId).then((d) => {
      setForm({
        lot_number: d.lot_number, category: d.category, lot_name: d.lot_name,
        remarks: d.remarks || "", video_url: d.video_url || "", status: d.manual_status || "",
      });
      setItems(d.items.length ? d.items.map((it) => ({ ...emptyItem(), ...it, price_per_unit: it.price_per_unit ?? "" })) : [emptyItem()]);
      setMedia((d.media || []).filter((m) => m.media_type === "image").map((m) => ({ id: m.id, url: m.url, is_primary: m.is_primary })));
    }).catch(() => setError("Could not load lot")).finally(() => setLoading(false));
  }, [lotId, isEdit]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setItem = (i, k, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((p) => [...p, emptyItem()]);
  const removeItem = (i) => setItems((p) => (p.length === 1 ? p : p.filter((_, idx) => idx !== i)));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setError("");
    try {
      for (const f of files) {
        const m = await uploadMedia(f);
        setMedia((p) => [...p, { id: m.id, url: m.url, is_primary: p.length === 0 }]);
      }
    } catch (err) {
      setError(err?.response?.data?.detail?.message || "Photo upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const setPrimary = (id) => setMedia((p) => p.map((m) => ({ ...m, is_primary: m.id === id })));
  const removeMedia = (id) => setMedia((p) => {
    const next = p.filter((m) => m.id !== id);
    if (next.length && !next.some((m) => m.is_primary)) next[0].is_primary = true;
    return next;
  });

  const canSave = form.lot_number.trim() && form.lot_name.trim() && form.category && items.length > 0;

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true); setError("");
    const payload = {
      lot_number: form.lot_number.trim(),
      category: form.category,
      lot_name: form.lot_name.trim(),
      remarks: form.remarks || null,
      video_url: form.video_url || null,
      status: form.status || null,
      items: items.map((it, idx) => ({
        ...it,
        original_quantity: Number(it.original_quantity) || 0,
        available_quantity: Number(it.available_quantity) || 0,
        price_per_unit: it.price_per_unit === "" || it.price_per_unit === null ? null : Number(it.price_per_unit),
        sort_order: idx,
      })),
      media: media.map((m, idx) => ({ id: m.id, is_primary: !!m.is_primary, sort_order: idx })),
    };
    try {
      const saved = isEdit ? await updateLot(lotId, payload) : await createLot(payload);
      onSaved(saved);
    } catch (err) {
      setError(err?.response?.data?.detail?.message || "Could not save lot");
      setSaving(false);
    }
  };

  const fields = CATEGORY_FIELDS[form.category] || CATEGORY_FIELDS.other;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center overflow-y-auto p-3" data-testid="b2b-lot-editor">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl my-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="font-bold text-slate-900">{isEdit ? "Edit Lot" : "Add Lot"}</h2>
          <button onClick={onClose} data-testid="editor-close" className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-400"><Loader2 className="inline animate-spin" size={20} /> Loading…</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Lot-level */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block text-sm">Category *
                <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="editor-category" className={inputCls}>
                  {CATS.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                </select>
              </label>
              <label className="block text-sm">Lot Number *
                <input value={form.lot_number} onChange={(e) => set("lot_number", e.target.value)} data-testid="editor-lot-number" placeholder="L-1025" className={inputCls} />
              </label>
              <label className="block text-sm sm:col-span-2">Lot / Product Name *
                <input value={form.lot_name} onChange={(e) => set("lot_name", e.target.value)} data-testid="editor-lot-name" placeholder="Mixed Laptop Lot" className={inputCls} />
              </label>
              <label className="block text-sm">Status
                <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="editor-status" className={inputCls}>
                  {MANUAL_STATUS_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </label>
              <label className="block text-sm">Video URL
                <input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} data-testid="editor-video" placeholder="YouTube / Drive / direct link" className={inputCls} />
              </label>
              <label className="block text-sm sm:col-span-2">Lot Remarks
                <textarea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} data-testid="editor-remarks" className={inputCls} />
              </label>
            </div>

            {/* Photos */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-800">Photos</span>
                <label className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50" data-testid="editor-upload">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Add photos
                  <input type="file" accept="image/*" multiple hidden onChange={onUpload} disabled={uploading} />
                </label>
              </div>
              {media.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {media.map((m) => (
                    <div key={m.id} className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-100">
                      <img src={mediaSrc(m.url)} alt="" className="h-full w-full object-cover" />
                      <button onClick={() => setPrimary(m.id)} title="Set as primary"
                        className={`absolute top-1 left-1 rounded-full p-1 ${m.is_primary ? "bg-amber-400 text-white" : "bg-white/80 text-slate-500"}`}>
                        <Star size={12} fill={m.is_primary ? "currentColor" : "none"} />
                      </button>
                      <button onClick={() => removeMedia(m.id)} title="Remove"
                        className="absolute top-1 right-1 rounded-full p-1 bg-white/80 text-rose-600">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-800">Line Items ({items.length})</span>
                <button onClick={addItem} data-testid="editor-add-item" className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 text-white px-3 py-1.5 text-sm hover:bg-slate-800">
                  <Plus size={14} /> Add line
                </button>
              </div>
              <div className="space-y-3">
                {items.map((it, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 p-3" data-testid={`editor-item-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-500">Line {i + 1}</span>
                      <button onClick={() => removeItem(i)} disabled={items.length === 1}
                        className="text-rose-500 hover:text-rose-700 disabled:opacity-30" data-testid={`editor-remove-item-${i}`}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {fields.map((f) => (
                        <label key={f.key} className="block text-xs text-slate-500">{f.label}
                          <input value={it[f.key] ?? ""} placeholder={f.placeholder || ""}
                            onChange={(e) => setItem(i, f.key, e.target.value)}
                            data-testid={`editor-item-${i}-${f.key}`} className={inputCls} />
                        </label>
                      ))}
                      <label className="block text-xs text-slate-500">Original Qty
                        <input type="number" min={0} value={it.original_quantity}
                          onChange={(e) => setItem(i, "original_quantity", e.target.value)}
                          data-testid={`editor-item-${i}-orig`} className={inputCls} />
                      </label>
                      <label className="block text-xs text-slate-500">Available Qty
                        <input type="number" min={0} value={it.available_quantity}
                          onChange={(e) => setItem(i, "available_quantity", e.target.value)}
                          data-testid={`editor-item-${i}-avail`} className={inputCls} />
                      </label>
                      <label className="block text-xs text-slate-500">Price / pc (₹)
                        <input type="number" min={0} step="0.01" value={it.price_per_unit}
                          onChange={(e) => setItem(i, "price_per_unit", e.target.value)}
                          data-testid={`editor-item-${i}-price`} className={inputCls} />
                      </label>
                      <label className="block text-xs text-slate-500 sm:col-span-1">Remarks
                        <input value={it.remarks ?? ""} onChange={(e) => setItem(i, "remarks", e.target.value)}
                          data-testid={`editor-item-${i}-remarks`} className={inputCls} />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="editor-error">{error}</div>}

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={save} disabled={!canSave || saving} data-testid="editor-save"
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-40">
                {saving && <Loader2 size={14} className="animate-spin" />} {isEdit ? "Save Changes" : "Save & Publish"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
